import { useState, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown, Shield, GraduationCap, Building2, Bot, ShoppingBag, Check, BookOpen, Calendar, FileText, HelpCircle, FolderOpen, Target, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supportedLanguages, languageLabels, languageNames, type SupportedLanguage } from '@/i18n';
import { useLocalePath } from '@/hooks/useLocalePath';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTheme } from '@/contexts/ThemeContext';
import ThemeToggle from '@/components/ui/ThemeToggle';
import greeninLogoDark from '@/assets/greenin-impact-logo-dark.svg';
import greeninLogoLight from '@/assets/greenin-impact-logo-light.svg';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProgramsOpen, setIsProgramsOpen] = useState(false);
  const [isResourcesOpen, setIsResourcesOpen] = useState(false);
  const [isMobileProgramsOpen, setIsMobileProgramsOpen] = useState(false);
  const [isMobileResourcesOpen, setIsMobileResourcesOpen] = useState(false);
  const { language, setLanguage, t } = useLanguage();
  const { resolvedTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const lp = useLocalePath();
  
  const logo = resolvedTheme === 'dark' ? greeninLogoDark : greeninLogoLight;

  const programPages = [
    { 
      key: 'programs.card1.title', 
      descKey: 'programs.card1.description',
      path: '/programs/safe-sustainable-communities', 
      icon: Shield,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      hoverBg: 'hover:bg-primary/5'
    },
    { 
      key: 'programs.card2.title', 
      descKey: 'programs.card2.description',
      path: '/programs/green-talent-digital-future', 
      icon: GraduationCap,
      color: 'text-secondary',
      bgColor: 'bg-secondary/10',
      hoverBg: 'hover:bg-secondary/5'
    },
    { 
      key: 'programs.card3.title', 
      descKey: 'programs.card3.description',
      path: '/programs/purpose-driven-smes', 
      icon: Building2,
      color: 'text-accent',
      bgColor: 'bg-accent/10',
      hoverBg: 'hover:bg-accent/5'
    },
    { 
      key: 'programs.card4.title', 
      descKey: 'programs.card4.description',
      path: '/programs/ai-for-good-responsible-ai', 
      icon: Bot,
      color: 'text-chart-3',
      bgColor: 'bg-chart-3/10',
      hoverBg: 'hover:bg-chart-3/5'
    },
    { 
      key: 'programs.card5.title', 
      descKey: 'programs.card5.description',
      path: '/programs/circular-impact-greenin-shop', 
      icon: ShoppingBag,
      color: 'text-chart-4',
      bgColor: 'bg-chart-4/10',
      hoverBg: 'hover:bg-chart-4/5'
    },
  ];

  const resourcePages = [
    { 
      key: 'nav.library', 
      descKey: 'nav.library.desc',
      href: '/library', 
      icon: BookOpen,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      hoverBg: 'hover:bg-primary/5'
    },
    { 
      key: 'nav.publicActivity', 
      descKey: 'nav.publicActivity.desc',
      href: '/public', 
      icon: Calendar,
      color: 'text-secondary',
      bgColor: 'bg-secondary/10',
      hoverBg: 'hover:bg-secondary/5'
    },
    { 
      key: 'nav.faq', 
      descKey: 'nav.faq.desc',
      href: '/#faq', 
      icon: HelpCircle,
      color: 'text-accent',
      bgColor: 'bg-accent/10',
      hoverBg: 'hover:bg-accent/5',
      isAnchor: true
    },
    { 
      key: 'nav.sdgs', 
      descKey: 'nav.sdgs.desc',
      href: '/sdgs', 
      icon: Target,
      color: 'text-chart-3',
      bgColor: 'bg-chart-3/10',
      hoverBg: 'hover:bg-chart-3/5'
    },
  ];

  const navItems = [
    { key: 'nav.home', href: '/#home', isAnchor: true },
    { key: 'nav.programs', href: '/#programs', isAnchor: true, hasDropdown: true },
    { key: 'nav.aiForGood', href: '/#ai-good', isAnchor: true },
    { key: 'nav.circularImpact', href: '/#circular', isAnchor: true },
    { key: 'nav.transparency', href: '/#transparency', isAnchor: true },
    { key: 'nav.resources', href: '#', isAnchor: false, hasResourcesDropdown: true },
    { key: 'nav.getInvolved', href: '/#get-involved', isAnchor: true },
    { key: 'nav.contact', href: '/#contact', isAnchor: true },
  ];

  const isOnProgramPage = programPages.some(p => location.pathname.endsWith(p.path));
  const currentProgramPath = location.pathname;

  const handleLanguageChange = (lang: SupportedLanguage) => {
    setLanguage(lang);
  };

  const handleKeyDown = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action();
    }
  };

  const handleNavClick = useCallback((e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>, href: string) => {
    e.preventDefault();
    
    const scrollToSection = (sectionId: string) => {
      const attemptScroll = (attempts = 0) => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        } else if (attempts < 10) {
          setTimeout(() => attemptScroll(attempts + 1), 50 * (attempts + 1));
        }
      };
      attemptScroll();
    };
    
    if (href.startsWith('/#')) {
      const sectionId = href.replace('/#', '');
      const homePath = lp('/');
      
      if (!location.pathname.match(/^\/(en|es|pt|fr)\/?$/)) {
        navigate(homePath);
        setTimeout(() => scrollToSection(sectionId), 150);
      } else {
        scrollToSection(sectionId);
      }
    } else if (href.startsWith('#')) {
      const sectionId = href.replace('#', '');
      scrollToSection(sectionId);
    }
  }, [location.pathname, navigate]);

  const renderNavLink = (item: typeof navItems[0]) => {
    const href = location.pathname === '/' ? item.href.replace('/', '') : item.href;
    
    if (item.hasDropdown) {
      return (
        <DropdownMenu open={isProgramsOpen} onOpenChange={setIsProgramsOpen}>
          <DropdownMenuTrigger asChild>
            <button
              className={`flex items-center gap-1 text-sm font-medium transition-colors link-underline focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background rounded px-1 ${
                isOnProgramPage 
                  ? 'text-primary' 
                  : 'text-foreground/80 hover:text-primary'
              }`}
              aria-expanded={isProgramsOpen}
              aria-haspopup="true"
            >
              {t(item.key)}
              {isOnProgramPage && (
                <span className="w-1.5 h-1.5 rounded-full bg-primary ml-1" aria-hidden="true" />
              )}
              <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isProgramsOpen ? 'rotate-180' : ''}`} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent 
            align="start" 
            className="w-80 bg-popover border-border z-50 p-2"
            sideOffset={8}
          >
            <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wider px-2 py-1">
              {t('common.strategicPrograms')}
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="my-2" />
            <div className="space-y-1">
              {programPages.map((program) => {
                const isActive = currentProgramPath.endsWith(program.path);
                return (
                  <DropdownMenuItem 
                    key={program.path} 
                    asChild
                    className="p-0 focus:bg-transparent"
                  >
                    <Link 
                      to={lp(program.path)}
                      className={`group flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-all duration-200 ${
                        isActive 
                          ? `${program.bgColor} border border-border/50` 
                          : `${program.hoverBg} hover:translate-x-1`
                      }`}
                      onClick={() => setIsProgramsOpen(false)}
                    >
                      <div className={`w-9 h-9 rounded-lg ${program.bgColor} flex items-center justify-center shrink-0 transition-transform duration-200 group-hover:scale-110`}>
                        <program.icon className={`w-5 h-5 ${program.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-medium ${isActive ? program.color : 'text-foreground'}`}>
                            {t(program.key)}
                          </span>
                          {isActive && (
                            <Check className={`w-4 h-4 ${program.color}`} aria-label={t('common.currentPage')} />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2 transition-opacity duration-200 opacity-70 group-hover:opacity-100">
                          {t(program.descKey)}
                        </p>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                );
              })}
            </div>
            <DropdownMenuSeparator className="my-2" />
            <DropdownMenuItem asChild className="p-0 focus:bg-transparent">
              <a 
                href={href}
                onClick={(e) => {
                  handleNavClick(e, item.href);
                  setIsProgramsOpen(false);
                }}
                className="flex items-center justify-center gap-2 p-3 rounded-lg cursor-pointer text-primary hover:bg-primary/5 transition-all duration-200 hover:translate-x-1"
              >
                <span className="text-sm font-medium">
                  {t('common.viewAllPrograms')}
                </span>
              </a>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }

    // Resources dropdown
    if (item.hasResourcesDropdown) {
      const isOnResourcePage = resourcePages.some(r => !r.isAnchor && location.pathname === r.href);
      return (
        <DropdownMenu open={isResourcesOpen} onOpenChange={setIsResourcesOpen}>
          <DropdownMenuTrigger asChild>
            <button
              className={`flex items-center gap-1 text-sm font-medium transition-colors link-underline focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background rounded px-1 ${
                isOnResourcePage 
                  ? 'text-primary' 
                  : 'text-foreground/80 hover:text-primary'
              }`}
              aria-expanded={isResourcesOpen}
              aria-haspopup="true"
            >
              <FolderOpen className="w-4 h-4" />
              {t(item.key)}
              {isOnResourcePage && (
                <span className="w-1.5 h-1.5 rounded-full bg-primary ml-1" aria-hidden="true" />
              )}
              <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isResourcesOpen ? 'rotate-180' : ''}`} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent 
            align="start" 
            className="w-72 bg-popover border-border z-50 p-2"
            sideOffset={8}
          >
            <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wider px-2 py-1">
              {t('common.resourcesKnowledge')}
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="my-2" />
            <div className="space-y-1">
              {resourcePages.map((resource) => {
                const isActive = !resource.isAnchor && location.pathname === resource.href;
                
                if (resource.isAnchor) {
                  return (
                    <DropdownMenuItem 
                      key={resource.href} 
                      asChild
                      className="p-0 focus:bg-transparent"
                    >
                      <a 
                        href={location.pathname === '/' ? resource.href.replace('/', '') : resource.href}
                        onClick={(e) => {
                          handleNavClick(e, resource.href);
                          setIsResourcesOpen(false);
                        }}
                        className={`group flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-all duration-200 ${resource.hoverBg} hover:translate-x-1`}
                      >
                        <div className={`w-9 h-9 rounded-lg ${resource.bgColor} flex items-center justify-center shrink-0 transition-transform duration-200 group-hover:scale-110`}>
                          <resource.icon className={`w-5 h-5 ${resource.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-foreground">
                            {t(resource.key)}
                          </span>
                          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2 transition-opacity duration-200 opacity-70 group-hover:opacity-100">
                            {t(resource.descKey)}
                          </p>
                        </div>
                      </a>
                    </DropdownMenuItem>
                  );
                }
                
                return (
                  <DropdownMenuItem 
                    key={resource.href} 
                    asChild
                    className="p-0 focus:bg-transparent"
                  >
                    <Link 
                      to={lp(resource.href)}
                      className={`group flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-all duration-200 ${
                        isActive 
                          ? `${resource.bgColor} border border-border/50` 
                          : `${resource.hoverBg} hover:translate-x-1`
                      }`}
                      onClick={() => setIsResourcesOpen(false)}
                    >
                      <div className={`w-9 h-9 rounded-lg ${resource.bgColor} flex items-center justify-center shrink-0 transition-transform duration-200 group-hover:scale-110`}>
                        <resource.icon className={`w-5 h-5 ${resource.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-medium ${isActive ? resource.color : 'text-foreground'}`}>
                            {t(resource.key)}
                          </span>
                          {isActive && (
                            <Check className={`w-4 h-4 ${resource.color}`} aria-label={t('common.currentPage')} />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2 transition-opacity duration-200 opacity-70 group-hover:opacity-100">
                          {t(resource.descKey)}
                        </p>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                );
              })}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }

    // For non-anchor links (internal pages)
    if (!item.isAnchor) {
      return (
        <Link
          to={lp(item.href)}
          className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors link-underline focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background rounded px-1"
        >
          {t(item.key)}
        </Link>
      );
    }

    return (
      <a
        href={href}
        onClick={(e) => handleNavClick(e, item.href)}
        className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors link-underline focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background rounded px-1"
      >
        {t(item.key)}
      </a>
    );
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass" role="banner">
      <div className="container mx-auto px-3 sm:px-4 py-2 sm:py-3">
        <div className="flex items-center justify-between gap-2 xl:gap-3 2xl:gap-4">
          {/* Logo Lockup */}
          <Link 
            to={lp('/')}
            className="flex items-center shrink-0 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background rounded-lg"
            aria-label={t('common.logoAriaLabel')}
          >
            <img
              src={logo}
              alt={t('common.logoAlt')}
              className="h-9 sm:h-11 md:h-12 xl:h-11 2xl:h-12 w-auto max-w-[140px] sm:max-w-[170px] md:max-w-[200px] xl:max-w-[165px] 2xl:max-w-[190px]"
              width={190}
              height={48}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex items-center gap-1 xl:gap-1 2xl:gap-2 flex-1 justify-center" role="navigation" aria-label="Main navigation">
            {navItems.map((item) => (
              <div key={item.key} className="shrink-0">
                {renderNavLink(item)}
              </div>
            ))}
          </nav>

          {/* Right side actions */}
          <div className="flex items-center gap-1 sm:gap-1.5">
            {/* Theme Toggle */}
            <ThemeToggle />

            {/* Language Selector - Premium Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-1.5 min-h-[44px] min-w-[44px] px-2.5 sm:px-3 py-2 rounded-xl bg-muted/40 border border-border/40 hover:bg-muted/70 hover:border-border/60 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background group"
                  aria-label={`${languageLabels[language]} – ${t('common.selectLanguage')}`}
                >
                  <Globe className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors duration-200" />
                  <span className="text-xs font-bold tracking-wider text-foreground/90">{languageLabels[language]}</span>
                  <ChevronDown className="w-3 h-3 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-48 bg-popover/95 backdrop-blur-xl border-border/50 shadow-xl shadow-black/10 p-1.5 rounded-xl"
                sideOffset={8}
              >
                <DropdownMenuLabel className="text-[10px] text-muted-foreground uppercase tracking-[0.15em] font-semibold px-2.5 py-1.5">
                  {t('common.selectLanguage')}
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="my-1 opacity-50" />
                {supportedLanguages.map((lang) => (
                  <DropdownMenuItem
                    key={lang}
                    onClick={() => handleLanguageChange(lang)}
                    className={`flex items-center justify-between gap-3 px-2.5 py-2.5 rounded-lg cursor-pointer transition-all duration-200 min-h-[44px] ${
                      language === lang
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'hover:bg-muted/60 text-foreground/80 hover:text-foreground'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`text-xs font-bold tracking-wider w-7 text-center py-0.5 rounded ${
                        language === lang
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted/80 text-muted-foreground'
                      }`}>
                        {languageLabels[lang]}
                      </span>
                      <span className="text-sm">{languageNames[lang]}</span>
                    </div>
                    {language === lang && (
                      <Check className="w-4 h-4 text-primary shrink-0" />
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile/Tablet Menu Toggle - WCAG 44px touch target */}
            <button
              onClick={() => {
                setIsMenuOpen(!isMenuOpen);
                if (!isMenuOpen) {
                  setIsMobileProgramsOpen(false);
                  setIsMobileResourcesOpen(false);
                }
              }}
              className="xl:hidden p-2.5 sm:p-2.5 rounded-lg hover:bg-muted/50 transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label={isMenuOpen ? t('common.closeMenu') : t('common.openMenu')}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
            >
              {isMenuOpen ? <X className="w-5 h-5 sm:w-6 sm:h-6" /> : <Menu className="w-5 h-5 sm:w-6 sm:h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile/Tablet Menu - Enhanced */}
        {isMenuOpen && (
          <nav 
            id="mobile-menu"
            className="xl:hidden mt-3 pb-4 border-t border-border/50 pt-4 animate-fade-up max-h-[calc(100vh-80px)] overflow-y-auto"
            role="navigation"
            aria-label="Mobile navigation"
          >
            <div className="flex flex-col gap-1">
              {navItems.map((item) => {
                const href = location.pathname === '/' ? item.href.replace('/', '') : item.href;
                
                {/* Programs collapsible section */}
                if (item.hasDropdown) {
                  return (
                    <div key={item.key} className="space-y-1">
                      <button
                        onClick={() => setIsMobileProgramsOpen(!isMobileProgramsOpen)}
                        className={`w-full flex items-center justify-between min-h-[48px] px-3 rounded-xl text-sm font-semibold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-ring ${
                          isOnProgramPage 
                            ? 'text-primary bg-primary/5' 
                            : 'text-foreground/80 hover:text-primary hover:bg-muted/50'
                        }`}
                        aria-expanded={isMobileProgramsOpen}
                      >
                        <span className="flex items-center gap-2">
                          {t(item.key)}
                          {isOnProgramPage && (
                            <span className="w-2 h-2 rounded-full bg-primary" aria-hidden="true" />
                          )}
                        </span>
                        <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isMobileProgramsOpen ? 'rotate-180' : ''}`} />
                      </button>
                      
                      {isMobileProgramsOpen && (
                        <div className="ml-2 pl-3 border-l-2 border-primary/20 space-y-1 animate-fade-up">
                          {programPages.map((program) => {
                            const isActive = currentProgramPath.endsWith(program.path);
                            return (
                              <Link
                                key={program.path}
                                to={lp(program.path)}
                                onClick={() => setIsMenuOpen(false)}
                                className={`flex items-center gap-3 min-h-[48px] text-sm transition-all duration-200 px-3 rounded-xl ${
                                  isActive 
                                    ? `${program.bgColor} ${program.color} font-medium` 
                                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
                                }`}
                              >
                                <div className={`w-8 h-8 rounded-lg ${program.bgColor} flex items-center justify-center shrink-0`}>
                                  <program.icon className={`w-4 h-4 ${program.color}`} />
                                </div>
                                <span className="flex-1">{t(program.key)}</span>
                                {isActive && <Check className={`w-4 h-4 ${program.color} shrink-0`} />}
                              </Link>
                            );
                          })}
                          {/* View all programs link */}
                          <a
                            href={href}
                            onClick={(e) => {
                              handleNavClick(e, item.href);
                              setIsMenuOpen(false);
                            }}
                            className="flex items-center justify-center min-h-[44px] text-sm font-medium text-primary hover:bg-primary/5 rounded-xl transition-all duration-200"
                          >
                            {t('common.viewAllPrograms')}
                          </a>
                        </div>
                      )}
                    </div>
                  );
                }

                {/* Resources collapsible section */}
                if (item.hasResourcesDropdown) {
                  const isOnResourcePage = resourcePages.some(r => !r.isAnchor && location.pathname === r.href);
                  return (
                    <div key={item.key} className="space-y-1">
                      <button
                        onClick={() => setIsMobileResourcesOpen(!isMobileResourcesOpen)}
                        className={`w-full flex items-center justify-between min-h-[48px] px-3 rounded-xl text-sm font-semibold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-ring ${
                          isOnResourcePage 
                            ? 'text-primary bg-primary/5' 
                            : 'text-foreground/80 hover:text-primary hover:bg-muted/50'
                        }`}
                        aria-expanded={isMobileResourcesOpen}
                      >
                        <span className="flex items-center gap-2">
                          <FolderOpen className="w-4 h-4" />
                          {t(item.key)}
                          {isOnResourcePage && (
                            <span className="w-2 h-2 rounded-full bg-primary" aria-hidden="true" />
                          )}
                        </span>
                        <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isMobileResourcesOpen ? 'rotate-180' : ''}`} />
                      </button>
                      
                      {isMobileResourcesOpen && (
                        <div className="ml-2 pl-3 border-l-2 border-primary/20 space-y-1 animate-fade-up">
                          {resourcePages.map((resource) => {
                            const isActive = !resource.isAnchor && location.pathname === resource.href;
                            
                            if (resource.isAnchor) {
                              return (
                                <a
                                  key={resource.href}
                                  href={location.pathname === '/' ? resource.href.replace('/', '') : resource.href}
                                  onClick={(e) => {
                                    handleNavClick(e, resource.href);
                                    setIsMenuOpen(false);
                                  }}
                                  className="flex items-center gap-3 min-h-[48px] text-sm transition-all duration-200 px-3 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted/30"
                                >
                                  <div className={`w-8 h-8 rounded-lg ${resource.bgColor} flex items-center justify-center shrink-0`}>
                                    <resource.icon className={`w-4 h-4 ${resource.color}`} />
                                  </div>
                                  <span className="flex-1">{t(resource.key)}</span>
                                </a>
                              );
                            }
                            
                            return (
                              <Link
                                key={resource.href}
                                to={lp(resource.href)}
                                onClick={() => setIsMenuOpen(false)}
                                className={`flex items-center gap-3 min-h-[48px] text-sm transition-all duration-200 px-3 rounded-xl ${
                                  isActive 
                                    ? `${resource.bgColor} ${resource.color} font-medium` 
                                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
                                }`}
                              >
                                <div className={`w-8 h-8 rounded-lg ${resource.bgColor} flex items-center justify-center shrink-0`}>
                                  <resource.icon className={`w-4 h-4 ${isActive ? resource.color : ''}`} />
                                </div>
                                <span className="flex-1">{t(resource.key)}</span>
                                {isActive && <Check className={`w-4 h-4 ${resource.color} shrink-0`} />}
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }
                
                {/* Regular nav links */}
                if (!item.isAnchor) {
                  return (
                    <Link
                      key={item.key}
                      to={lp(item.href)}
                      onClick={() => setIsMenuOpen(false)}
                      className="min-h-[48px] flex items-center px-3 rounded-xl text-sm font-medium text-foreground/80 hover:text-primary hover:bg-muted/50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      {t(item.key)}
                    </Link>
                  );
                }
                
                return (
                  <a
                    key={item.key}
                    href={href}
                    onClick={(e) => {
                      handleNavClick(e, item.href);
                      setIsMenuOpen(false);
                    }}
                    className="min-h-[48px] flex items-center px-3 rounded-xl text-sm font-medium text-foreground/80 hover:text-primary hover:bg-muted/50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    {t(item.key)}
                  </a>
                );
              })}

              {/* Separator before CTA */}
              <div className="my-2 border-t border-border/50" />

              {/* CTA Button */}
              <Button variant="hero" className="w-full min-h-[48px] text-sm" asChild>
                <a 
                  href={location.pathname === '/' ? '#get-involved' : '/#get-involved'} 
                  onClick={(e) => {
                    handleNavClick(e, '/#get-involved');
                    setIsMenuOpen(false);
                  }}
                >
                  {t('nav.cta')}
                </a>
              </Button>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
